/**
 * @fileoverview TanStack Query hooks for the manual Meta CSV import.
 * @module features/ad-spends/hooks/useMetaCsvImport
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { commitMetaImport, getMetaImportCoverage, previewMetaImport } from '../api/adSpendImportService'
import type { ImportCoverage, MetaImportPreview, MetaImportResult } from '../schemas/adSpendImportSchema'

export const META_IMPORT_COVERAGE_QUERY_KEY = ['ad-spend-import', 'meta', 'coverage'] as const

/** Query keys whose data depends on stored ad spend or on the Needs review inbox. */
export function isAffectedByAdSpendImport(queryKey: readonly unknown[]): boolean {
  return (
    queryKey[0] === 'ad-spend-import' ||
    queryKey.includes('statistics') ||
    queryKey.includes('needs-review') ||
    queryKey.includes('daily-summary')
  )
}

export function useMetaImportCoverage(): UseQueryResult<ImportCoverage, Error> {
  const { activeOrgId } = useActiveOrg()
  return useQuery<ImportCoverage, Error>({
    queryKey: [...META_IMPORT_COVERAGE_QUERY_KEY, activeOrgId],
    queryFn: getMetaImportCoverage,
    enabled: Boolean(activeOrgId),
  })
}

export function usePreviewMetaImport(): UseMutationResult<MetaImportPreview, Error, readonly File[]> {
  return useMutation<MetaImportPreview, Error, readonly File[]>({ mutationFn: previewMetaImport })
}

export function useCommitMetaImport(): UseMutationResult<MetaImportResult, Error, readonly File[]> {
  const queryClient = useQueryClient()
  return useMutation<MetaImportResult, Error, readonly File[]>({
    mutationFn: commitMetaImport,
    onSuccess: () => {
      void queryClient.invalidateQueries({ predicate: query => isAffectedByAdSpendImport(query.queryKey) })
    },
  })
}
